import {
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/view/components/ui/alert-dialog";
import { Button } from "@/view/components/ui/button";
import { Spinner } from "@/view/components/ui/spinner";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState, useEffect } from "react";
import ValuesAndCommissionsForm, {
  partnerConfigs,
} from "@/view/components/ValuesAndCommissionsForm";
import { useEditPartnerController } from "./useEditPartnerController";
import { useAuth } from "@/app/hooks/useAuth";
import { BasicInput } from "@/view/components/BasicInput";
import { TriStateEnum } from "@/app/entities/TriState";
import { PartnerType, getPartnerLabel } from "@/app/utils/partners.enum";

interface EditPartnerProps {
  partnerData?: {
    partner: string;
    itens: { label: string | number; value: number }[];
    tableId?: string;
    delay?: number;
    isActive?: boolean;
    state?: TriStateEnum;
  } | null;
}

export const EditPartner = ({ partnerData }: EditPartnerProps) => {
  const {
    handleSubmit,
    initialValues,
    isCrefaz,
    isSalvcar,
    isPan,
    partnerData: data,
    formSchema,
  } = useEditPartnerController({ partnerData });

  const { isSuperAdmin } = useAuth();
  const [hasMaxError, setHasMaxError] = useState(false);

  const methods = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      delay: String(partnerData?.delay ?? 0),
    },
  });

  useEffect(() => {
    const subscription = methods.watch((formValues) => {
      const valuesAndCommissions = formValues.valuesAndCommissions;

      if (!valuesAndCommissions?.length) {
        setHasMaxError(false);
        return;
      }
      const hasError = valuesAndCommissions.some((item: any) => {
        if (!item?.max) return false;

        const commission =
          Number(String(item.commission || "0").replace(/\D/g, "")) / 100;
        const max = Number(String(item.max).replace(/\D/g, "")) / 100;

        return commission > 0 && commission > max;
      });

      setHasMaxError(hasError);
    });

    return () => subscription.unsubscribe();
  }, [methods]);

  const onFormSubmit = async (formData: any) => {
    const result = await handleSubmit(formData);

    if (result?.invalidIndexes) {
      result.invalidIndexes.forEach((index) => {
        methods.setValue(`valuesAndCommissions.${index}.leaderCommission`, "");
        methods.setValue(`valuesAndCommissions.${index}.commission`, "");
        methods.setValue(`valuesAndCommissions.${index}.max`, "");
      });
    }
  };

  if (!data) return null;

  return (
    <AlertDialogContent className="max-w-[600px]">
      <AlertDialogHeader>
        <AlertDialogTitle>
          Configurações {getPartnerLabel(data.partner as PartnerType)}
        </AlertDialogTitle>
      </AlertDialogHeader>

      {(isCrefaz || isSalvcar || isPan) && (
        <div className="mb-4 text-sm text-black04">
          <p>
            {isCrefaz
              ? "A comissão da Crefaz é calculada como uma porcentagem sobre o valor total contratado. O percentual varia conforme o número de parcelas, seguindo as faixas configuradas abaixo."
              : isPan
                ? "A comissão é calculada como uma porcentagem sobre o valor total contratado, independente do número de parcelas."
                : "A comissão da Salvcar é definida como um valor fixo, determinada conforme a tabela abaixo."}
          </p>
          {hasMaxError && (
            <p className="mt-2 text-sm text-error">
              A comissão não pode exceder o valor máximo definido para cada
              faixa.
            </p>
          )}
        </div>
      )}

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onFormSubmit)}>
          <ValuesAndCommissionsForm
            initialValues={initialValues}
            isCrefaz={isCrefaz}
            isOpen={true}
            isSuperAdmin={isSuperAdmin}
            partner={data.partner as keyof typeof partnerConfigs}
          />

          {isSuperAdmin && (
            <div className="mb-4">
              <BasicInput
                name="delay"
                label="Bloqueio de saque (Dias)"
                style={{ backgroundColor: "#F7F7F7" }}
                maxLength={3}
                onInput={(e) => {
                  e.currentTarget.value = e.currentTarget.value.replace(
                    /\D/g,
                    "",
                  );
                }}
              />
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <Button
              type="submit"
              className="bg-primary text-white01"
              disabled={methods.formState.isSubmitting || hasMaxError}
            >
              {methods.formState.isSubmitting ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />
                  Salvando...
                </>
              ) : (
                "Salvar alterações"
              )}
            </Button>
          </AlertDialogFooter>
        </form>
      </FormProvider>
    </AlertDialogContent>
  );
};
